// Единственный метод запроса полного экрана, доступный в этом браузере.
// Safari (в том числе на iPad) до 16.4 знает только префиксную версию,
// а на iPhone полноэкранного режима для обычных элементов нет вовсе —
// там метода не будет ни одного
const getRequestFullscreenMethod = (element) => {
  if (!element) return null;
  return element.requestFullscreen
    || element.webkitRequestFullscreen
    || element.mozRequestFullScreen
    || element.msRequestFullscreen
    || null;
};

const getExitFullscreenMethod = () => {
  return document.exitFullscreen
    || document.webkitExitFullscreen
    || document.mozCancelFullScreen
    || document.msExitFullscreen
    || null;
};

// События смены полноэкранного режима во всех вариантах префиксов: выйти
// из него можно не только кнопкой, но и по Esc, системным жестом или
// кнопкой «Назад» на Android
export const FULLSCREEN_CHANGE_EVENTS = [
  'fullscreenchange', 'webkitfullscreenchange', 'mozfullscreenchange', 'MSFullscreenChange',
];

export const getFullscreenElement = () => {
  return document.fullscreenElement
    || document.webkitFullscreenElement
    || document.mozFullScreenElement
    || document.msFullscreenElement;
};

// Поддерживается ли полноэкранный режим в принципе: есть метод и документ
// его не запрещает. Проверять обязательно ПЕРЕД показом кнопки — на iPhone
// (Fullscreen API там только для <video>) она была живой, но нажатие на нее
// не давало ничего
export const isFullscreenSupported = () => {
  if (!getRequestFullscreenMethod(document.documentElement)) return false;
  const isEnabled = document.fullscreenEnabled
    ?? document.webkitFullscreenEnabled
    ?? document.mozFullScreenEnabled
    ?? document.msFullscreenEnabled;
  // Свойства нет — считаем, что не запрещено (запрет приходит только явным false)
  return isEnabled !== false;
};

// Всегда возвращает промис и никогда не бросает исключение синхронно.
// Префиксные реализации WebKit промис НЕ возвращают, и .catch() у
// вызывающего кода падал на undefined, обрывая обработчик клика целиком
export const requestFullscreen = (element) => {
  const request = getRequestFullscreenMethod(element);
  if (!request) return Promise.reject(new Error('Fullscreen API not supported'));
  try {
    // Без Element.ALLOW_KEYBOARD_INPUT: в актуальных браузерах клавиатура в
    // полноэкранном режиме разрешена по умолчанию, а WebKit на вызов с этим
    // аргументом местами молча отказывал
    return Promise.resolve(request.call(element));
  } catch (error) {
    return Promise.reject(error);
  }
};

export const exitFullscreen = () => {
  const exit = getExitFullscreenMethod();
  if (!exit) return Promise.reject(new Error('Fullscreen API not supported'));
  try {
    return Promise.resolve(exit.call(document));
  } catch (error) {
    return Promise.reject(error);
  }
};

// Запущено ли приложение как установленное PWA (с домашнего экрана или из
// списка приложений), а не обычной вкладкой браузера.
//
// display-mode: fullscreen здесь НЕ подходит: этот режим включает и сам игрок
// кнопкой полного экрана в настройках — иначе за установку приложения
// засчитывался бы обычный полноэкранный режим (награда, фильтр в админке и
// рекламная цель установки). В манифесте display: standalone, поэтому у
// настоящего установленного приложения этой ветки и не бывает.
//
// iOS Safari W3C-медиазапрос display-mode не поддерживает вовсе — там об
// установленном приложении сообщает только navigator.standalone
export const isRunningAsInstalledPwa = () => {
  if (window.navigator.standalone) return true;
  return window.matchMedia('(display-mode: standalone), (display-mode: minimal-ui)').matches;
};
